/**
 * TanStack Query hooks for email operations
 *
 * Provides data fetching, caching, and mutations for emails
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { useEmailMutations } from "@stockline/web";
import client from "@/app/api";
import { Email, EmailList, PageOptions } from "@/app/types";
import { emailKeys, EmailFilters } from "../keys/emails";

const DELTA_BUFFER_MS = 5_000;
const DELTA_INTERVAL_MS = 30_000;

/**
 * Fetch emails with optional filtering
 */
export function useEmails(
  filters?: EmailFilters,
  options?: Omit<UseQueryOptions<EmailList>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: emailKeys.list(filters),
    queryFn: () =>
      client.emails.getEmails({
        page: filters?.page || 1,
        size: filters?.size || 200,
      }),
    staleTime: 1000 * 60 * 2, // 2 minutes
    ...options,
  });
}

/**
 * Fetch emails with pre-orders included, using delta polling.
 *
 * - First fetch: full (no modified_since)
 * - Subsequent: delta only (modified_since = last server_time - 5s buffer)
 * - 0 items returned → same ref → no rerender
 * - Also picks up emails with new AI-created pre-orders
 * - When search is active, always does full fetch (result set changes)
 */
export function useEmailsWithPreOrders(filters?: EmailFilters) {
  const serverTimeRef = useRef<string | null>(null);
  const cachedResultRef = useRef<EmailList | null>(null);

  const queryFingerprint = `${filters?.page}|${filters?.size}|${filters?.includeArchived}|${filters?.archived}|${filters?.search}`;
  const prevFingerprintRef = useRef(queryFingerprint);

  const fetchWithDelta = useCallback(async (): Promise<EmailList> => {
    // Reset on fingerprint change
    if (prevFingerprintRef.current !== queryFingerprint) {
      serverTimeRef.current = null;
      cachedResultRef.current = null;
      prevFingerprintRef.current = queryFingerprint;
    }

    // Skip delta when search is active (result set changes each time)
    let modifiedSince: string | undefined;
    if (serverTimeRef.current && !filters?.search) {
      const since = new Date(
        new Date(serverTimeRef.current).getTime() - DELTA_BUFFER_MS,
      );
      modifiedSince = since.toISOString().slice(0, -1);
    }

    const result = await client.emails.getEmailsWithPreOrders({
      page: filters?.page || 1,
      size: filters?.size || 50,
      include_archived: filters?.includeArchived || false,
      archived_only: filters?.archived === true,
      search: filters?.search,
      modified_since: modifiedSince,
    });

    serverTimeRef.current = result.server_time ?? serverTimeRef.current;

    let mergedResult: EmailList;

    if (modifiedSince && cachedResultRef.current) {
      if (result.items.length === 0) {
        // Nothing changed → return same reference → no rerender
        // Still update unread_count as it may change independently
        if (result.unread_count !== cachedResultRef.current.unread_count) {
          cachedResultRef.current = {
            ...cachedResultRef.current,
            unread_count: result.unread_count,
          };
        }
        return cachedResultRef.current;
      }
      // Merge changed items into cache
      const changedIds = new Set(result.items.map((e) => e.id));
      const kept = cachedResultRef.current.items.filter(
        (e) => !changedIds.has(e.id),
      );
      const mergedItems = [...kept, ...result.items].sort(
        (a, b) =>
          new Date(b.email_date ?? 0).getTime() -
          new Date(a.email_date ?? 0).getTime(),
      );
      mergedResult = {
        ...cachedResultRef.current,
        items: mergedItems,
        unread_count: result.unread_count,
      };
    } else {
      // Full fetch (first load, fingerprint change, or search active)
      mergedResult = result;
    }

    cachedResultRef.current = mergedResult;
    return mergedResult;
  }, [
    queryFingerprint,
    filters?.page,
    filters?.size,
    filters?.includeArchived,
    filters?.archived,
    filters?.search,
  ]);

  return useQuery({
    queryKey: emailKeys.withPreOrders(filters),
    queryFn: fetchWithDelta,
    placeholderData: (previousData) => previousData,
    staleTime: DELTA_INTERVAL_MS,
    refetchInterval: () => (document.hidden ? 5 * 60_000 : DELTA_INTERVAL_MS),
    refetchIntervalInBackground: true,
  });
}

/**
 * Fetch emails with pre-orders using infinite query for pagination
 */
export function useInfiniteEmailsWithPreOrders(filters?: EmailFilters) {
  const pageSize = filters?.size || 50;

  return useInfiniteQuery<EmailList, Error>({
    queryKey: emailKeys.withPreOrdersInfinite(filters),
    queryFn: ({ pageParam }) =>
      client.emails.getEmailsWithPreOrders({
        page: pageParam as number,
        size: pageSize,
        include_archived: false,
        archived_only: filters?.archived === true,
        search: filters?.search,
      }),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.page;
      const totalPages = Math.ceil(lastPage.total / lastPage.page_size);
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 1,
    staleTime: 1000 * 60, // 1 minute
    refetchInterval: () => (document.hidden ? 5 * 60_000 : 60_000),
    refetchIntervalInBackground: true,
  });
}

/**
 * Fetch single email by ID
 */
export function useEmail(
  emailId: string | null,
  options?: Omit<UseQueryOptions<Email>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: emailKeys.detail(emailId!),
    queryFn: () => client.emails.getEmail(emailId!),
    enabled: !!emailId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
}

/**
 * Archive email mutation
 *
 * The email list reads from Dexie via `useOfflineEmails()` and filters on
 * `archived` locally. The TanStack cache below is consulted only by legacy
 * surfaces. Without an optimistic Dexie write the UI freezes on archive
 * until the SSE round-trip lands. See the equivalent block in `useSMS.ts`
 * for the full rationale.
 */
export function useArchiveEmail() {
  const queryClient = useQueryClient();
  const { setArchivedOptimistic } = useEmailMutations();

  return useMutation({
    mutationFn: (emailId: string) => client.emails.archiveEmail(emailId),
    onMutate: async (emailId) => {
      const rollback = await setArchivedOptimistic(emailId, true);

      // Cancel all email queries
      await queryClient.cancelQueries({ queryKey: emailKeys.all });

      // Get ALL email-related queries (with any filters)
      const allEmailQueries = queryClient.getQueriesData<EmailList>({
        queryKey: emailKeys.all,
      });

      // Store the archived email to add to archived view
      let archivedEmail: any = null;

      // Optimistically update ALL matching queries
      allEmailQueries.forEach(([queryKey]) => {
        queryClient.setQueryData<EmailList>(queryKey, (old) => {
          if (!old || !old.items) return old;

          // Find the email being archived (before we remove it)
          if (!archivedEmail) {
            archivedEmail = old.items.find(
              (email: any) => email.id === emailId,
            );
          }

          // Check if this is an archived view
          const keyStr = JSON.stringify(queryKey);
          const isArchivedView = keyStr.includes('"archived":true');

          if (isArchivedView && archivedEmail) {
            // For archived views, ADD the archived email at the top
            return {
              ...old,
              items: [{ ...archivedEmail, archived: true }, ...old.items],
              total: old.total + 1,
            };
          } else {
            // For non-archived views, REMOVE the archived email (show 9 instead of 10)
            return {
              ...old,
              items: old.items.filter((email: any) => email.id !== emailId),
              total: old.total - 1,
            };
          }
        });
      });

      return { allEmailQueries, rollback };
    },
    onSuccess: async (data, emailId, context) => {
      // Silently refetch both views to ensure everything is in sync
      await queryClient.refetchQueries({
        queryKey: emailKeys.all,
        type: "active",
      });
    },
    onError: (err, emailId, context) => {
      console.error("Failed to archive email:", err);
      if (context?.rollback) void context.rollback();
      // Revert all queries on error
      if (context?.allEmailQueries) {
        context.allEmailQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
}

/**
 * Unarchive email mutation
 */
export function useUnarchiveEmail() {
  const queryClient = useQueryClient();
  const { setArchivedOptimistic } = useEmailMutations();

  return useMutation({
    mutationFn: (emailId: string) => client.emails.unarchiveEmail(emailId),
    onMutate: async (emailId) => {
      const rollback = await setArchivedOptimistic(emailId, false);

      // Cancel all email queries
      await queryClient.cancelQueries({ queryKey: emailKeys.all });

      // Get ALL email-related queries (with any filters)
      const allEmailQueries = queryClient.getQueriesData<EmailList>({
        queryKey: emailKeys.all,
      });

      // First pass: find the email being unarchived from the archived view
      let unarchivedEmail: any = null;
      allEmailQueries.forEach(([queryKey, data]) => {
        if (!unarchivedEmail && data?.items) {
          const keyStr = JSON.stringify(queryKey);
          const isArchivedView = keyStr.includes('"archived":true');
          if (isArchivedView) {
            unarchivedEmail = data.items.find(
              (email: any) => email.id === emailId,
            );
            if (unarchivedEmail) {
            }
          }
        }
      });

      // Second pass: update ALL matching queries
      allEmailQueries.forEach(([queryKey]) => {
        queryClient.setQueryData<EmailList>(queryKey, (old) => {
          if (!old || !old.items) return old;

          // Check if this is an archived view
          const keyStr = JSON.stringify(queryKey);
          const isArchivedView = keyStr.includes('"archived":true');
          const isNonArchivedView = keyStr.includes('"archived":false');

          if (isArchivedView) {
            // For archived views, REMOVE the unarchived email (show 9 instead of 10)
            return {
              ...old,
              items: old.items.filter((email: any) => email.id !== emailId),
              total: old.total - 1,
            };
          } else if (isNonArchivedView && unarchivedEmail) {
            // For non-archived views, ADD the unarchived email at the top
            return {
              ...old,
              items: [{ ...unarchivedEmail, archived: false }, ...old.items],
              total: old.total + 1,
            };
          }
          return old;
        });
      });

      return { allEmailQueries, rollback };
    },
    onSuccess: async (data, emailId, context) => {
      // Silently refetch both views to ensure everything is in sync
      await queryClient.refetchQueries({
        queryKey: emailKeys.all,
        type: "active",
      });
    },
    onError: (err, emailId, context) => {
      console.error("Failed to unarchive email:", err);
      if (context?.rollback) void context.rollback();
      // Revert all queries on error
      if (context?.allEmailQueries) {
        context.allEmailQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
}

/**
 * Batch archive emails mutation
 */
export function useArchiveEmailsBatch() {
  const queryClient = useQueryClient();
  const { setArchivedOptimistic } = useEmailMutations();

  return useMutation({
    mutationFn: (emailIds: string[]) =>
      client.emails.archiveEmailsBatch(emailIds),
    onMutate: async (emailIds) => {
      const rollbacks = await Promise.all(
        emailIds.map((id) => setArchivedOptimistic(id, true)),
      );

      // Cancel all email queries
      await queryClient.cancelQueries({ queryKey: emailKeys.all });

      const emailIdSet = new Set(emailIds);

      // Get ALL email-related queries (with any filters)
      const allEmailQueries = queryClient.getQueriesData<EmailList>({
        queryKey: emailKeys.all,
      });

      // Store the archived emails to add to archived view
      let archivedEmails: any[] = [];

      // Optimistically update ALL matching queries
      allEmailQueries.forEach(([queryKey]) => {
        queryClient.setQueryData<EmailList>(queryKey, (old) => {
          if (!old || !old.items) return old;

          // Find emails being archived (before we remove them)
          if (archivedEmails.length === 0) {
            archivedEmails = old.items.filter((email: any) =>
              emailIdSet.has(email.id),
            );
          }

          // Check if this is an archived view by inspecting the query key
          const keyStr = JSON.stringify(queryKey);
          const isArchivedView = keyStr.includes('"archived":true');

          if (isArchivedView && archivedEmails.length > 0) {
            // For archived views, ADD the archived emails at the top
            const updatedEmails = archivedEmails.map((email: any) => ({
              ...email,
              archived: true,
            }));
            return {
              ...old,
              items: [...updatedEmails, ...old.items],
              total: old.total + emailIds.length,
            };
          } else {
            // For non-archived views, REMOVE the archived emails
            const filteredItems = old.items.filter(
              (email: any) => !emailIdSet.has(email.id),
            );
            return {
              ...old,
              items: filteredItems,
              total: old.total - emailIds.length,
            };
          }
        });
      });

      return { allEmailQueries, rollbacks };
    },
    onSuccess: async (data, emailIds, context) => {
      // Silently refetch both views to ensure everything is in sync
      await queryClient.refetchQueries({
        queryKey: emailKeys.all,
        type: "active",
      });
    },
    onError: (err, emailIds, context) => {
      console.error("Failed to archive emails:", err);
      if (context?.rollbacks) {
        for (const rollback of context.rollbacks) {
          if (rollback) void rollback();
        }
      }
      // Revert all queries on error
      if (context?.allEmailQueries) {
        context.allEmailQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
}

/**
 * Mark email as read mutation
 */
export function useMarkEmailAsRead() {
  const queryClient = useQueryClient();
  const { setReadOptimistic } = useEmailMutations();

  return useMutation({
    mutationFn: (emailId: string) => client.emails.markEmailAsRead(emailId),
    onMutate: async (emailId) => {
      const rollback = await setReadOptimistic(emailId, true);

      await queryClient.cancelQueries({ queryKey: emailKeys.all });

      const allEmailQueries = queryClient.getQueriesData({
        queryKey: emailKeys.all,
      });

      // Update both flat (EmailList) and infinite ({ pages: EmailList[] }) queries
      allEmailQueries.forEach(([queryKey]) => {
        queryClient.setQueryData(queryKey, (old: any) => {
          if (!old) return old;
          // Infinite query: { pages: EmailList[], pageParams: [...] }
          if (old.pages) {
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                items: page.items?.map((email: any) =>
                  email.id === emailId ? { ...email, read: true } : email,
                ),
              })),
            };
          }
          // Flat query: EmailList { items: [...] }
          if (old.items) {
            return {
              ...old,
              items: old.items.map((email: any) =>
                email.id === emailId ? { ...email, read: true } : email,
              ),
            };
          }
          return old;
        });
      });

      return { allEmailQueries, rollback };
    },
    onSuccess: () => {
      // Don't invalidate - we already optimistically updated the data
    },
    onError: (_err, _emailId, context) => {
      if (context?.rollback) void context.rollback();
      if (context?.allEmailQueries) {
        context.allEmailQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
}

/**
 * Mark email as unread mutation
 */
export function useMarkEmailAsUnread() {
  const queryClient = useQueryClient();
  const { setReadOptimistic } = useEmailMutations();

  return useMutation({
    mutationFn: (emailId: string) => client.emails.markEmailAsUnread(emailId),
    onMutate: async (emailId) => {
      const rollback = await setReadOptimistic(emailId, false);

      await queryClient.cancelQueries({ queryKey: emailKeys.all });

      const allEmailQueries = queryClient.getQueriesData({
        queryKey: emailKeys.all,
      });

      // Update both flat (EmailList) and infinite ({ pages: EmailList[] }) queries
      allEmailQueries.forEach(([queryKey]) => {
        queryClient.setQueryData(queryKey, (old: any) => {
          if (!old) return old;
          // Infinite query: { pages: EmailList[], pageParams: [...] }
          if (old.pages) {
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                items: page.items?.map((email: any) =>
                  email.id === emailId ? { ...email, read: false } : email,
                ),
              })),
            };
          }
          // Flat query: EmailList { items: [...] }
          if (old.items) {
            return {
              ...old,
              items: old.items.map((email: any) =>
                email.id === emailId ? { ...email, read: false } : email,
              ),
            };
          }
          return old;
        });
      });

      return { allEmailQueries, rollback };
    },
    onSuccess: () => {
      // Don't invalidate - we already optimistically updated the data
    },
    onError: (_err, _emailId, context) => {
      if (context?.rollback) void context.rollback();
      if (context?.allEmailQueries) {
        context.allEmailQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
}

/**
 * Download email attachment mutation
 */
export function useDownloadEmailAttachment() {
  return useMutation({
    mutationFn: ({
      emailId,
      attachmentName,
    }: {
      emailId: string;
      attachmentName: string;
    }) => client.emails.downloadEmailAttachment(emailId, attachmentName),
    onSuccess: (blob, variables) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Use the attachment name as filename
      link.download = variables.attachmentName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}
